import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useTrackEvent } from '../../context/AnalyticsContext';

// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import {
  isFieldForCategory,
  pickCategoryFields,
} from '../../util/fieldHelpers.js';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';
import WishlistButton from '../../components/WishlistButton';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import Notifications from './Notifications/Notifications';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionGallery from './SectionGallery';
import SectionShippingPolicyMaybe from './SectionShippingPolicyMaybe';
import SectionPurchaseProtection from './SectionPurchaseProtection';
import SectionDimensionsMaybe from './SectionDimensionsMaybe';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionText from '../../components/CustomExtendedDataSection/SectionText';
import CustomListingFields from './CustomListingFields';
import ListingPageAccessWrapper from './ListingPageAccessWrapper';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

// Hand drawn arrow SVG component for SmartPreviewBanner
const HandDrawnArrow = () => (
  <svg
    viewBox="0 0 100 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    strokeWidth="15"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block ml-1 w-5 h-5"
  >
    <line x1="50" y1="10" x2="50" y2="100" />
    <polyline points="35 85 50 100 65 85" />
  </svg>
);

// Smart Preview Beta Banner component
const SmartPreviewBanner = ({ onTryItClick }) => (
  <div className="flex justify-center w-full mb-4">
    <div
      className="flex items-center gap-3 md:gap-4 px-5 py-3 md:px-6 md:py-4 rounded-xl w-full"
      style={{ background: 'linear-gradient(135deg, rgba(124, 51, 234, 0.12) 0%, rgba(124, 51, 234, 0.06) 100%)' }}
    >
      <img
        src="/static/images/mockupIcon.png"
        alt=""
        className="w-8 h-8 md:w-12 md:h-12 flex-shrink-0"
      />
      <div className="flex gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="m-0 text-sm md:text-sm text-gray-600 leading-snug">
            Not sure how it'll look? See it on your wall before you buy. Use our virtual preview tool in seconds.
          </p>
        </div>
      </div>
      {/* Try it link */}
      <button
        onClick={onTryItClick}
        className="mx-1 md:flex items-center flex-shrink-0 font-bold bg-transparent border-none p-0 cursor-pointer underline text-purple-900 hover:text-purple-600 text-2xl"
        style={{ fontFamily: "'Caveat', cursive" }}
      >
        <span>try it</span>
        <HandDrawnArrow />
      </button>
    </div>
  </div>
);

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [viewStartTime] = useState(Date.now());
  const [galleryControls, setGalleryControls] = useState(null);

  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    variants,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    onFetchTransactionLineItems,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

  const parentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const allListings = useMemo(() => {
    return [parentListing, ...(Array.isArray(variants) ? variants : [])];
  }, [parentListing, variants]);

  const [selectedVariantId, setSelectedVariantId] = useState(parentListing.id?.uuid);

  // Derive currentListing dynamically based on selectedVariantId and allListings
  const currentListing = useMemo(() => {
    const selected = allListings.find(listing => listing.id?.uuid === selectedVariantId);
    if (!selected || selected.id?.uuid === parentListing.id?.uuid) {
      return parentListing;
    }

    // For variants, only take specific fields and use parent listing for the rest
    return { ...selected, attributes: { ...selected.attributes, title: parentListing.attributes.title } };
  }, [allListings, selectedVariantId, parentListing]);

  // Use ref to store latest values for tracking to avoid unnecessary effect re-runs
  const trackingDataRef = useRef();
  trackingDataRef.current = {
    trackEvent,
    currentListing,
    currentUser,
    viewStartTime,
  };

  // Track time spent on listing detail - only re-runs when listing ID changes
  useEffect(() => {
    const id = currentListing?.id?.uuid;
    if (!id) return;

    return () => {
      const data = trackingDataRef.current;
      const timeSpent = Math.floor((Date.now() - data.viewStartTime) / 1000);
      if (timeSpent > 3) {
        const currentAuthor = data.currentListing?.author;
        const publicData = data.currentListing?.attributes?.publicData || {};
        const price = data.currentListing?.attributes?.price;
        const authorAvailable = data.currentListing && data.currentListing.author;
        const userAndListingAuthorAvailable = !!(data.currentUser && authorAvailable);
        const isOwnListing =
          userAndListingAuthorAvailable &&
          data.currentListing.author?.id?.uuid === data.currentUser?.id?.uuid;

        data.trackEvent('listing_detail_engagement', {
          listing_id: id,
          time_spent: timeSpent,
          artist_id: currentAuthor?.id?.uuid,
          listing_type: publicData.listingType,
          price: price?.amount,
          is_own_listing: isOwnListing,
        });
      }
    };
  }, [currentListing?.id?.uuid]);

  // Gallery navigation callbacks - must be before early returns
  const handleGalleryReady = useCallback(controls => {
    setGalleryControls(controls);
  }, []);

  const handleTryMockupClick = useCallback(() => {
    if (galleryControls?.slideToMockup) {
      galleryControls.slideToMockup();
    }
  }, [galleryControls]);

  const handleVariantChange = newVariantId => {
    setSelectedVariantId(newVariantId);

    // Always fetch line items when the variant changes
    onFetchTransactionLineItems({
      orderData: { stockReservationQuantity: 1 },
      listingId: new UUID(newVariantId),
      isOwnListing: false,
    });

    // Track variant selection
    if (newVariantId !== parentListing.id.uuid) {
      trackEvent('listing_variant_selected', {
        parent_listing_id: parentListing.id.uuid,
        selected_variant_id: newVariantId,
        artist_id: currentListing.author?.id?.uuid,
        listing_type: currentListing.attributes.publicData?.listingType,
      });
    }
  };

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own listings. This
  // will fail with 403 Forbidden if the author is another user.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing (e.g. 403 forbidden, unpublished, etc.)
    // Return proper 404 to avoid soft 404 in Google Search Console
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!currentListing.id) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author?.id?.uuid === currentUser?.id?.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType.
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }

  const listingTypeConfig = config.listing.listingTypes.find(
    conf => conf.listingType === listingType
  );

  // Category filtering for SectionDetailsMaybe
  const { key: categoryPrefix, categories: listingCategoriesConfig } = config.categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);
  const isFieldForSelectedCategories = fieldConfig => isFieldForCategory(currentCategories, fieldConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  const mockupReadyImageId = publicData?.mockupReadyImageId;
  const showMockupFeatures = !!mockupReadyImageId;
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    currentListing,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const authorProfileURL = ensuredAuthor?.attributes?.profile?.publicData?.username
    ? `${config.marketplaceRootURL}/artist/${ensuredAuthor.attributes.profile.publicData.username}`
    : null;
  const COUNTRY_CODES = { IL: 'Israel', US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia', FR: 'France', DE: 'Germany' };
  const countryCode = ensuredAuthor?.attributes?.profile?.publicData?.locationCountry || null;
  const countryOfOrigin = countryCode ? (COUNTRY_CODES[countryCode] || countryCode) : null;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  // Canonical path always includes the slug for SEO-friendly URLs.
  // This ensures /l/id (ListingPageCanonical) and /l/slug/id both
  // produce the same canonical, avoiding "Alternate page with proper canonical tag" in GSC.
  const canonicalListingPath = `/l/${listingSlug}/${listingId.uuid}`;

  // Noindex UUID-only listing URLs (no slug in params) and variant listings
  // to reduce crawl budget waste and avoid duplicate/thin content indexing
  const isUuidOnlyUrl = !rawParams.slug;
  const isVariantListing = !!publicData.isVariant || !!publicData.parentId;
  const shouldNoIndex = isUuidOnlyUrl || isVariantListing;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      canonicalPath={canonicalListingPath}
      noIndex={shouldNoIndex}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: title,
        image: schemaImages,
        ...(authorDisplayName ? { brand: { '@type': 'Brand', name: authorDisplayName } } : {}),
        ...(authorDisplayName ? {
          seller: {
            '@type': 'Person',
            name: authorDisplayName,
            ...(authorProfileURL ? { url: authorProfileURL } : {}),
          },
        } : {}),
        ...(countryOfOrigin ? { countryOfOrigin: { '@type': 'Country', name: countryOfOrigin } } : {}),
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            <Notifications
              mounted={mounted}
              listing={currentListing}
              isOwnListing={isOwnListing}
              noPayoutDetailsSetWithOwnListing={noPayoutDetailsSetWithOwnListing}
              currentUser={currentUser}
              className={css.actionBarForProductLayout}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
              }}
            />
            {showMockupFeatures && <SmartPreviewBanner onTryItClick={handleTryMockupClick} />}
            <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
              currentUser={currentUser}
              parentListing={parentListing}
              onGalleryReady={handleGalleryReady}
            />
            <div className={css.mobileHeading}>
              <H4 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H4>
            </div>

            {/* Beta message and contact button - mobile only */}
            {!isOwnListing && currentListing.attributes.state !== LISTING_STATE_CLOSED && (
              <div className={css.betaMessageMobile}>
                <p className={css.betaText}>
                  <FormattedMessage id="OrderPanel.betaMessage" />
                </p>
                {onContactUser && (
                  <button
                    className={css.contactButton}
                    onClick={() => onContactUser(ensuredAuthor)}
                  >
                    <FormattedMessage id="OrderPanel.contactSeller" />
                  </button>
                )}
              </div>
            )}

            {/* WishlistButton - mobile only */}
            {!isOwnListing &&
              currentListing.attributes.state !== LISTING_STATE_CLOSED &&
              currentListing?.id && (
                <div className={css.wishlistButtonContainerMobile}>
                  <WishlistButton
                    listingId={parentListing?.id?.uuid || currentListing?.id?.uuid}
                    showText={true}
                    className={css.wishlistButton}
                  />
                </div>
              )}

            {/* 1. Description */}
            <SectionText text={description} showAsIngress />

            {/* 2. Dimensions */}
            <SectionDimensionsMaybe publicData={publicData} />

            {/* 3. Details */}
            <SectionDetailsMaybe
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
              hideDimensions={true}
              isFieldForCategory={isFieldForSelectedCategories}
            />

            {/* 4. Shipping & Returns */}
            <SectionShippingPolicyMaybe
              publicData={publicData}
              listingTypeConfig={listingTypeConfig}
              intl={intl}
            />

            {/* 5. Joodaic Purchase Protection */}
            <SectionPurchaseProtection />

            {/* 6–8. Art Medium, Materials, Color */}
            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
              skipDetailsMaybe={true}
            />

            {/* 9. Reviews */}
            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={css.productOrderPanel}
              onVariantChange={handleVariantChange}
              listing={currentListing}
              variants={[parentListing, ...(Array.isArray(variants) ? variants : [])]}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              reviewLink={
                reviews.length > 0 ? (
                  <a
                    href="#reviews"
                    className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-full px-2 py-0.5 inline-block"
                  >
                    Reviews ({reviews.length})
                  </a>
                ) : null
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @returns {JSX.Element} listing page component
 */
const ListingPage = props => {
  const dispatch = useDispatch();
  const store = useStore();

  const { isAuthenticated } = useSelector(state => state.auth);
  const {
    showListingError,
    reviews,
    variants,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = useSelector(state => state.ListingPage);
  const currentUser = useSelector(state => state.user?.currentUser);
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const getListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'listing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );
  const getOwnListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'ownListing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const callSetInitialValues = useCallback(
    (setInitialValuesFn, values, saveToSessionStorage) =>
      dispatch(setInitialValuesFn(values, saveToSessionStorage)),
    [dispatch]
  );
  const onFetchTransactionLineItems = useCallback(
    params => dispatch(fetchTransactionLineItems(params)),
    [dispatch]
  );
  const onSendInquiry = useCallback(
    (listing, message) => dispatch(sendInquiry(listing, message)),
    [dispatch]
  );
  const onInitializeCardPaymentData = useCallback(
    () => dispatch(initializeCardPaymentData()),
    [dispatch]
  );
  const onFetchTimeSlots = useCallback(
    (listingId, start, end, timeZone, options) =>
      dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
    [dispatch]
  );

  return (
    <ListingPageAccessWrapper
      {...props}
      PageComponent={ListingPageComponent}
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      getListing={getListing}
      getOwnListing={getOwnListing}
      scrollingDisabled={scrollingDisabled}
      inquiryModalOpenForListingId={inquiryModalOpenForListingId}
      showListingError={showListingError}
      variants={variants}
      reviews={reviews}
      fetchReviewsError={fetchReviewsError}
      monthlyTimeSlots={monthlyTimeSlots}
      timeSlotsForDate={timeSlotsForDate}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      sendInquiryInProgress={sendInquiryInProgress}
      sendInquiryError={sendInquiryError}
      onManageDisableScrolling={onManageDisableScrolling}
      callSetInitialValues={callSetInitialValues}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      onSendInquiry={onSendInquiry}
      onInitializeCardPaymentData={onInitializeCardPaymentData}
      onFetchTimeSlots={onFetchTimeSlots}
    />
  );
};

export default ListingPage;
